import { Badge, Button, DataTableColumnHeader, Text } from "@/components/ui";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import type { ExpenseViewModel } from "@/hooks/matter-view-models/use-expenses-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { Connection } from "@/lib/types";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDate } from "@/lib/utils/format";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { calculateExpenseTotals } from "@/lib/utils/matters/expense-utils";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { DollarSign } from "lucide-react";

// Define the extended type that includes the payee information
type ExpenseRowData = ExpenseViewModel & {
  expensePayee?: Connection;
  matterId: string;
};

const columnHelper = createColumnHelper<ExpenseRowData>();

// Component for the trust check button
const TrustCheckButton = ({
  matterId,
  expenseId,
}: {
  matterId: string;
  expenseId: string;
}) => {
  const { openTrustCheckModal } = useGlobalModal();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    openTrustCheckModal({
      matterId,
      trustCheckType: "Withdraw",
      editing: false,
      expenseId,
    });
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      className="h-8 w-8"
    >
      <DollarSign className="h-4 w-4" />
    </Button>
  );
};

export const matterExpensesColumns: ColumnDef<ExpenseRowData>[] = [
  columnHelper.accessor("dateCreated", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    sortingFn: "customDateTime",
    id: "Date Created",
    cell: (cell) => (
      <Text variant="subHeading">
        {formatDate(convertToLocalFromDatabaseDateTime(cell.getValue()))}
      </Text>
    ),
  }),
  columnHelper.accessor("expensePayee", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Payee",
    cell: ({ cell }) => {
      const payee = cell.getValue();
      return (
        <Text variant="subHeading">
          {payee
            ? (getConnectionContactName(payee.contact ?? undefined) ?? "")
            : "-"}
        </Text>
      );
    },
  }),
  columnHelper.accessor("label", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Label",
    enableHiding: false,
    cell: ({ cell }) => (
      <Text variant="body" className="font-semibold">
        {cell.getValue()}
      </Text>
    ),
  }),
  columnHelper.accessor("category", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Category",
    cell: ({ cell }) => {
      return <Badge>{cell.getValue()}</Badge>;
    },
  }),
  columnHelper.accessor("cost", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Original Amount",
    cell: ({ cell }) => (
      <Text variant="subHeading">
        <CurrencyWrapper value={cell.getValue() ?? undefined} />
      </Text>
    ),
  }),
  columnHelper.accessor("paidAmount", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Paid",
    cell: ({ cell }) => (
      <Text variant="subHeading">
        <CurrencyWrapper value={cell.getValue() ?? undefined} />
      </Text>
    ),
  }),
  columnHelper.accessor(
    (row) => {
      if (!row.cost) {
        return 0;
      }
      return row.cost - (row.paidAmount ?? 0);
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Balance Remaining",
      cell: ({ row }) => {
        if (!row.original.cost && !row.original.reducedAmount) {
          return "-";
        }
        const balanceRemaining = calculateExpenseTotals([
          {
            cost: row.original.cost,
            reducedAmount: row.original.reducedAmount,
            paidAmount: row.original.paidAmount,
            refunded: row.original.refunded,
            refundedAmount: row.original.refundedAmount,
          },
        ]).totalBalanceRemaining;
        return (
          <Text variant="subHeading">
            <CurrencyWrapper value={balanceRemaining} />
          </Text>
        );
      },
    },
  ),
  columnHelper.accessor("dateIncurred", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Date Incurred",
    sortingFn: "customDateTime",
    cell: (cell) => (
      <Text variant="subHeading">{formatDate(cell.getValue()) || "-"}</Text>
    ),
  }),
  columnHelper.accessor("description", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Description",
    cell: (cell) => <Text variant="subHeading">{cell.getValue() || "-"}</Text>,
  }),
  columnHelper.accessor("paidByMethod", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Paid By",
    cell: (cell) => <Text variant="subHeading">{cell.getValue() || "-"}</Text>,
  }),
  columnHelper.accessor("paidByMethodDetails", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Paid By Details",
    cell: (cell) => <Text variant="subHeading">{cell.getValue() || "-"}</Text>,
  }),
  columnHelper.accessor("refunded", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Refunded",
    cell: ({ cell }) => {
      const isRefunded = cell.getValue();
      return (
        <div className="flex items-center gap-2">
          {isRefunded ? (
            <Badge variant="default">Yes</Badge>
          ) : (
            <Badge variant="secondary">No</Badge>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor("refundedAmount", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Refunded Amount",
    cell: ({ cell, row }) => {
      const refundedAmount = cell.getValue();
      const isRefunded = row.original.refunded;

      if (!isRefunded) {
        return "-";
      }

      return (
        <Text variant="subHeading">
          <CurrencyWrapper value={refundedAmount ?? undefined} />
        </Text>
      );
    },
  }),
  {
    id: "Reduced Amount",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    accessorKey: "reducedAmount",
    cell: ({ row }) => {
      const value = row.original.reducedAmount;
      return (
        <Text variant="subHeading">
          <CurrencyWrapper value={value ?? undefined} />
        </Text>
      );
    },
  },
  // Trust checks count column
  {
    id: "Trust Checks",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: ({ row }) => {
      const trustChecks = (row.original as ExpenseViewModel).trustChecks;
      return <Text variant="subHeading">{trustChecks.length}</Text>;
    },
  },
  // New trust check column
  {
    id: "Actions",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: ({ row }) => {
      const matterId = row.original.matterId;
      const expenseId = row.original.id;
      return matterId ? (
        <TrustCheckButton matterId={matterId} expenseId={expenseId} />
      ) : null;
    },
  },
] as ColumnDef<ExpenseRowData>[];

// Function to create columns with reduced amount when on settlements page
export const createMatterExpensesColumns = (
  // TODO: Remove this
  isOnSettlementsPage: boolean = false,
): ColumnDef<ExpenseRowData>[] => {
  if (!isOnSettlementsPage) {
    return matterExpensesColumns;
  }

  // Add reduced amount column to the end
  const columns = matterExpensesColumns;

  return columns;
};
